import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import cn from 'classnames';
import { useCallback, useMemo, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AuthService from '../../services/auth.service';
import { NotificationsService } from '../../services/notifications.service';
import TokenService from '../../services/token.service';
import { useAppDispatch, useAppSelector } from '../../store/reducers';
import { setAccessToken } from '../../store/reducers/Auth/authSlice';
import { authenticateWallet, buildWalletsBucketThunk, connectWallet } from '../../store/reducers/Wallet/slice';
import * as log from '../../utils/log';
import * as Wallet from '../../wallet/api';
import Button from '../Button/Button';
import Paragraph from '../Typography/Paragraph/Paragraph';
import styles from './WalletBox.module.scss';
import { walletItems } from './WalletBoxIcons';
const WalletBox = ({ name, icon, stakeId, accessToken, refreshToken, isEnabled, isUpdating }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { darkMode } = useAppSelector((state) => state.settings);
    const { activeWallet: { address, name: stateActiveWalletName, isConnected } } = useAppSelector((state) => state.wallet);
    const [cookies] = useCookies();
    const [isConnecting, setIsConnecting] = useState(false);
    const dispatch = useAppDispatch();
    const isSameStakeIdWallet = stakeId === address;
    const connectedWallet = useMemo(() => name === stateActiveWalletName && isConnected, [name, stateActiveWalletName, isConnected]);
    const handleAuthenticate = useCallback(() => {
        if (isConnecting)
            return;
        setIsConnecting(true);
        const selectWallet = Wallet.selectWallet(name);
        NotificationsService.onWalletSelect(selectWallet);
        selectWallet
            .then((res) => {
            dispatch(authenticateWallet({ walletName: name, stakeId: res.stakeId }));
        })
            .catch((err) => {
            log.error(err);
        })
            .finally(() => {
            setIsConnecting(false);
        });
    }, [dispatch, isConnecting, name, navigate]);
    const handleSameStakeIdConnection = useCallback(() => {
        if (refreshToken && accessToken) {
            dispatch(connectWallet({ name, refreshToken, accessToken }));
        }
    }, [dispatch, refreshToken, accessToken, name]);
    const handleDiffStakeIdConnection = useCallback(() => {
        if (refreshToken && accessToken) {
            AuthService.setAuthTokens({ refreshToken, accessToken });
            TokenService.setTokens(accessToken, refreshToken);
            dispatch(setAccessToken(accessToken));
            dispatch(connectWallet({ name, refreshToken, accessToken })).then(() => {
                dispatch(buildWalletsBucketThunk({ walletItems, cookies }));
            });
        }
    }, [dispatch, accessToken, refreshToken, name]);
    const onClickFn = useMemo(() => !accessToken && !refreshToken
        ? handleAuthenticate
        : isSameStakeIdWallet
            ? handleSameStakeIdConnection
            : handleDiffStakeIdConnection, [
        accessToken,
        refreshToken,
        handleAuthenticate,
        isSameStakeIdWallet,
        handleSameStakeIdConnection,
        handleDiffStakeIdConnection
    ]);
    return (_jsxs("div", Object.assign({ onClick: onClickFn, className: cn(styles.element, 'd-flex align-items-center justify-content-between', darkMode && styles['isDarkMode'], isUpdating && styles.disabled) }, { children: [_jsxs("div", Object.assign({ className: 'd-flex align-items-center' }, { children: [_jsx("div", Object.assign({ className: styles.imgBox }, { children: _jsx("img", { src: icon, alt: "" }) })), _jsx(Paragraph, { className: styles.text, title: name.toLocaleUpperCase() })] })), connectedWallet && (_jsx("div", Object.assign({ className: styles.active }, { children: _jsxs("div", Object.assign({ className: 'd-flex flex-column' }, { children: [_jsx(Paragraph, { size: 1, color: 'grey', fontFamily: 'urbanist', className: styles.connected, title: t('wallet_is_connected') }), _jsx(Button, { size: 'auto', className: cn(styles.button), color: 8, text: t('reconnect') })] })) })))] })));
};
export default WalletBox;
